import { Button, Modal } from '@affine/component';
import { AuthService } from '@affine/core/modules/cloud';
import { OAuthProviderType } from '@affine/graphql';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { WorkbenchService } from '../../../workbench';

interface GeniusProduct {
  id: string;
  name: string;
  imageUrl: string | null;
  description: string | null;
}

interface FunnelCheck {
  geniusFunnelId: string;
  funnelName: string;
  existingDocId?: string;
  existingDocTitle?: string;
}

interface ImportGeniusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartFromScratch: () => void;
}

type Step = 'choose' | 'products' | 'duplicates';

export function ImportGeniusModal({
  open,
  onOpenChange,
  onStartFromScratch,
}: ImportGeniusModalProps) {
  const [step, setStep] = useState<Step>('choose');
  const [products, setProducts] = useState<GeniusProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateFunnels, setDuplicateFunnels] = useState<FunnelCheck[]>([]);
  const [duplicateActions, setDuplicateActions] = useState<
    Record<string, 'new' | 'overwrite'>
  >({});

  const workbench = useService(WorkbenchService).workbench;
  const auth = useService(AuthService);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('choose');
      setProducts([]);
      setSelectedProductId(null);
      setError(null);
      setIsImporting(false);
      setIsChecking(false);
      setDuplicateFunnels([]);
      setDuplicateActions({});
    }
  }, [open]);

  const silentReconnect = useCallback((): Promise<boolean> => {
    return new Promise(resolve => {
      let iframe: HTMLIFrameElement | null = null;
      let messageOrigin: string | null = null;
      let done = false;

      const cleanup = (success: boolean) => {
        if (done) return;
        done = true;
        window.clearTimeout(timeout);
        iframe?.remove();
        window.removeEventListener('message', onMessage);
        resolve(success);
      };

      const timeout = window.setTimeout(() => cleanup(false), 10_000);

      const onMessage = async (ev: MessageEvent) => {
        if (
          !messageOrigin ||
          ev.origin !== messageOrigin ||
          (ev.data as { type?: string })?.type !== 'authorization_response'
        )
          return;
        const response = ((ev.data as { response?: Record<string, string> })
          .response ?? {}) as Record<string, string>;
        if (response.error || !response.code) {
          cleanup(false);
          return;
        }
        try {
          await auth.signInOauth(
            response.code,
            response.state ?? '',
            OAuthProviderType.OIDC
          );
          cleanup(true);
        } catch {
          cleanup(false);
        }
      };
      window.addEventListener('message', onMessage);

      const redirectUri = `${window.location.origin}/oauth/callback`;
      auth
        .oauthPreflight(OAuthProviderType.OIDC, 'web', redirectUri, {
          prompt: 'none',
          response_mode: 'web_message',
          origin: window.location.origin,
          redirect_uri: redirectUri,
        })
        .then(({ url }) => {
          if (done) return;
          messageOrigin = new URL(url).origin;
          iframe = document.createElement('iframe');
          iframe.style.cssText =
            'position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none';
          iframe.src = url;
          document.body.appendChild(iframe);
        })
        .catch(() => cleanup(false));
    });
  }, [auth]);

  const fetchProducts = useCallback(async () => {
    setIsLoadingProducts(true);
    setError(null);
    try {
      const response = await fetch('/api/integrations/genius-products', {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Erro ${response.status}`);
      const data = (await response.json()) as
        | { code?: string; error?: string }
        | GeniusProduct[];

      if (!Array.isArray(data)) {
        if (data.code === 'SESSION_REFRESH_REQUIRED') {
          const reconnected = await silentReconnect();
          if (reconnected) {
            const r2 = await fetch('/api/integrations/genius-products', {
              credentials: 'include',
            });
            if (!r2.ok) throw new Error(`Erro ${r2.status}`);
            const d2 = (await r2.json()) as
              | { code?: string; error?: string }
              | GeniusProduct[];
            setProducts(Array.isArray(d2) ? d2 : []);
          } else {
            setError('SESSION_REFRESH_REQUIRED');
          }
          return;
        }
        setError(data.error ?? 'Erro ao carregar produtos');
        return;
      }
      setProducts(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erro ao carregar produtos'
      );
    } finally {
      setIsLoadingProducts(false);
    }
  }, [silentReconnect]);

  const handleImportGenius = useCallback(async () => {
    setStep('products');
    await fetchProducts();
  }, [fetchProducts]);

  const executeImport = useCallback(
    async (
      productId: string,
      actions?: Record<string, 'new' | 'overwrite'>
    ) => {
      setIsImporting(true);
      setError(null);
      try {
        const response = await fetch('/api/integrations/import-from-genius', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productId, duplicateActions: actions }),
        });
        if (!response.ok) throw new Error(`Erro ${response.status}`);

        const data = (await response.json()) as { docIds: string[] };
        onOpenChange(false);
        if (data.docIds.length > 0) {
          workbench.open(`/${data.docIds[0]}?sync=1`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao importar');
      } finally {
        setIsImporting(false);
      }
    },
    [onOpenChange, workbench]
  );

  const handleImport = useCallback(async () => {
    if (!selectedProductId) return;
    setIsChecking(true);
    setError(null);
    try {
      // Check for duplicates first
      const checkRes = await fetch('/api/integrations/check-genius-import', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: selectedProductId }),
      });
      if (!checkRes.ok) throw new Error(`Erro ${checkRes.status}`);
      const checkData = (await checkRes.json()) as {
        funnels?: FunnelCheck[];
        error?: string;
        code?: string;
      };

      if (checkData.code === 'SESSION_REFRESH_REQUIRED') {
        setError('SESSION_REFRESH_REQUIRED');
        return;
      }
      if (checkData.error) {
        setError(checkData.error);
        return;
      }

      const funnels = checkData.funnels ?? [];
      const hasDuplicates = funnels.some(f => f.existingDocId);

      if (hasDuplicates) {
        setDuplicateFunnels(funnels);
        const actions: Record<string, 'new' | 'overwrite'> = {};
        for (const f of funnels) {
          if (f.existingDocId) actions[f.geniusFunnelId] = 'overwrite';
        }
        setDuplicateActions(actions);
        setStep('duplicates');
        return;
      }

      // No duplicates — import directly
      await executeImport(selectedProductId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao verificar');
    } finally {
      setIsChecking(false);
    }
  }, [selectedProductId, executeImport]);

  const handleConfirmDuplicates = useCallback(async () => {
    if (!selectedProductId) return;
    await executeImport(selectedProductId, duplicateActions);
  }, [selectedProductId, duplicateActions, executeImport]);

  const handleStartFromScratch = useCallback(() => {
    onOpenChange(false);
    onStartFromScratch();
  }, [onOpenChange, onStartFromScratch]);

  const modalTitle =
    step === 'choose'
      ? 'Nova pagina'
      : step === 'duplicates'
        ? 'Funis ja importados'
        : 'Importar da Genius';

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={modalTitle}
      width={480}
    >
      {step === 'choose' ? (
        <ChooseStep
          onStartFromScratch={handleStartFromScratch}
          onImportGenius={handleImportGenius}
        />
      ) : step === 'duplicates' ? (
        <DuplicatesStep
          funnels={duplicateFunnels}
          actions={duplicateActions}
          onChangeAction={(funnelId, action) =>
            setDuplicateActions(prev => ({ ...prev, [funnelId]: action }))
          }
          onConfirm={handleConfirmDuplicates}
          onBack={() => setStep('products')}
          isImporting={isImporting}
        />
      ) : (
        <ProductsStep
          products={products}
          isLoading={isLoadingProducts}
          isImporting={isChecking}
          error={error}
          selectedProductId={selectedProductId}
          onSelectProduct={setSelectedProductId}
          onImport={handleImport}
          onBack={() => setStep('choose')}
        />
      )}
    </Modal>
  );
}

function ChooseStep({
  onStartFromScratch,
  onImportGenius,
}: {
  onStartFromScratch: () => void;
  onImportGenius: () => void | Promise<void>;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        padding: '8px 0 16px',
        justifyContent: 'center',
      }}
    >
      <OptionCard
        emoji="📄"
        label="Comecar do zero"
        onClick={onStartFromScratch}
      />
      <OptionCard
        emoji="✨"
        label="Importar da Genius"
        onClick={() => void onImportGenius()}
      />
    </div>
  );
}

function OptionCard({
  emoji,
  label,
  onClick,
}: {
  emoji: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        width: 160,
        height: 120,
        borderRadius: 12,
        border: '1px solid var(--affine-border-color)',
        background: 'var(--affine-background-primary-color)',
        cursor: 'pointer',
        transition: 'background 0.15s',
        fontSize: 14,
        fontWeight: 500,
        color: 'var(--affine-text-primary-color)',
      }}
      onMouseEnter={e =>
        ((e.currentTarget as HTMLButtonElement).style.background =
          'var(--affine-hover-color)')
      }
      onMouseLeave={e =>
        ((e.currentTarget as HTMLButtonElement).style.background =
          'var(--affine-background-primary-color)')
      }
    >
      <span style={{ fontSize: 32 }}>{emoji}</span>
      <span>{label}</span>
    </button>
  );
}

function DuplicatesStep({
  funnels,
  actions,
  onChangeAction,
  onConfirm,
  onBack,
  isImporting,
}: {
  funnels: FunnelCheck[];
  actions: Record<string, 'new' | 'overwrite'>;
  onChangeAction: (funnelId: string, action: 'new' | 'overwrite') => void;
  onConfirm: () => void | Promise<void>;
  onBack: () => void;
  isImporting: boolean;
}) {
  const duplicates = funnels.filter(f => f.existingDocId);
  const newCount = funnels.filter(f => !f.existingDocId).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          fontSize: 14,
          color: 'var(--affine-text-secondary-color)',
        }}
      >
        {duplicates.length} funil(is) ja importado(s). Escolha o que fazer:
      </div>

      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {duplicates.map(f => (
          <div
            key={f.geniusFunnelId}
            style={{
              padding: '12px 0',
              borderBottom: '1px solid var(--affine-border-color)',
            }}
          >
            <div
              style={{
                fontWeight: 500,
                fontSize: 14,
                color: 'var(--affine-text-primary-color)',
              }}
            >
              {f.funnelName}
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--affine-text-secondary-color)',
                marginBottom: 8,
              }}
            >
              Documento existente: {f.existingDocTitle}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => onChangeAction(f.geniusFunnelId, 'overwrite')}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  border:
                    actions[f.geniusFunnelId] === 'overwrite'
                      ? '2px solid var(--affine-primary-color)'
                      : '1px solid var(--affine-border-color)',
                  background:
                    actions[f.geniusFunnelId] === 'overwrite'
                      ? 'var(--affine-hover-color)'
                      : 'transparent',
                  color:
                    actions[f.geniusFunnelId] === 'overwrite'
                      ? 'var(--affine-primary-color)'
                      : 'var(--affine-text-secondary-color)',
                }}
              >
                Sobrescrever
              </button>
              <button
                type="button"
                onClick={() => onChangeAction(f.geniusFunnelId, 'new')}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  border:
                    actions[f.geniusFunnelId] === 'new'
                      ? '2px solid var(--affine-primary-color)'
                      : '1px solid var(--affine-border-color)',
                  background:
                    actions[f.geniusFunnelId] === 'new'
                      ? 'var(--affine-hover-color)'
                      : 'transparent',
                  color:
                    actions[f.geniusFunnelId] === 'new'
                      ? 'var(--affine-primary-color)'
                      : 'var(--affine-text-secondary-color)',
                }}
              >
                Importar como novo
              </button>
            </div>
          </div>
        ))}
      </div>

      {newCount > 0 && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--affine-text-secondary-color)',
          }}
        >
          + {newCount} funil(is) serao importados normalmente.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button onClick={onBack} size="default" disabled={isImporting}>
          Voltar
        </Button>
        <Button
          onClick={() => void onConfirm()}
          variant="primary"
          size="default"
          disabled={isImporting}
          loading={isImporting}
        >
          {isImporting ? 'Importando...' : 'Confirmar'}
        </Button>
      </div>
    </div>
  );
}

function ProductsStep({
  products,
  isLoading,
  isImporting,
  error,
  selectedProductId,
  onSelectProduct,
  onImport,
  onBack,
}: {
  products: GeniusProduct[];
  isLoading: boolean;
  isImporting: boolean;
  error: string | null;
  selectedProductId: string | null;
  onSelectProduct: (id: string) => void;
  onImport: () => void | Promise<void>;
  onBack: () => void;
}) {
  if (isLoading) {
    return (
      <div
        style={{
          padding: '32px 0',
          textAlign: 'center',
          color: 'var(--affine-text-secondary-color)',
        }}
      >
        Carregando produtos...
      </div>
    );
  }

  if (error === 'SESSION_REFRESH_REQUIRED') {
    return (
      <div style={{ padding: '16px 0' }}>
        <div
          style={{
            color: 'var(--affine-text-secondary-color)',
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          Nao foi possivel reconectar automaticamente. Reconecte sua conta para
          continuar.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            onClick={() => {
              window.location.href = '/oauth/login?provider=OIDC';
            }}
            variant="primary"
            size="default"
          >
            Reconectar conta
          </Button>
          <Button onClick={onBack} size="default">
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px 0' }}>
        <div
          style={{
            color: 'var(--affine-error-color)',
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          {error}
        </div>
        <Button onClick={onBack} size="default">
          Voltar
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div style={{ padding: '16px 0' }}>
        <div
          style={{
            color: 'var(--affine-text-secondary-color)',
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          Nenhum produto encontrado na Genius.
        </div>
        <Button onClick={onBack} size="default">
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 12,
          maxHeight: 320,
          overflowY: 'auto',
          padding: '4px 0',
        }}
      >
        {products.map(product => (
          <ProductCard
            key={product.id}
            product={product}
            selected={selectedProductId === product.id}
            onSelect={onSelectProduct}
          />
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button onClick={onBack} size="default" disabled={isImporting}>
          Voltar
        </Button>
        <Button
          onClick={() => void onImport()}
          variant="primary"
          size="default"
          disabled={!selectedProductId || isImporting}
          loading={isImporting}
        >
          {isImporting ? 'Verificando...' : 'Importar'}
        </Button>
      </div>
    </div>
  );
}

function ProductCard({
  product,
  selected,
  onSelect,
}: {
  product: GeniusProduct;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onSelect(product.id)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: 12,
        borderRadius: 10,
        border: selected
          ? '2px solid var(--affine-primary-color)'
          : '1px solid var(--affine-border-color)',
        background: selected
          ? 'var(--affine-hover-color)'
          : 'var(--affine-background-primary-color)',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 0.15s',
      }}
    >
      {product.imageUrl ? (
        <img
          src={product.imageUrl}
          alt={product.name}
          style={{
            width: 64,
            height: 64,
            borderRadius: 8,
            objectFit: 'cover',
          }}
        />
      ) : (
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 8,
            background: 'var(--affine-hover-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
          }}
        >
          📦
        </div>
      )}
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--affine-text-primary-color)',
          lineHeight: 1.3,
          wordBreak: 'break-word',
        }}
      >
        {product.name}
      </span>
    </button>
  );
}
